import { FirebaseError } from "firebase/app";
import {
  collection,
  connectFirestoreEmulator,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  type DocumentData,
  type Firestore,
} from "firebase/firestore/lite";

import { getFirebaseClient } from "../firebase/firebaseClient";
import { GAME_RESULT_LIMITS } from "./firestoreSchema";

export interface LeaderboardEntry {
  id: string;
  rank: number;
  alias: string;
  playerType: "guest" | "player";
  score: number;
  achievedAt: Date;
}

export interface OwnScoreSummary {
  bestScore: number | null;
  bestAchievedAt: Date | null;
  latestScore: number | null;
  latestFinishedAt: Date | null;
}

export interface LeaderboardRepository {
  loadTopTen(): Promise<LeaderboardEntry[]>;
  loadOwnScore(uid: string): Promise<OwnScoreSummary | null>;
}

let singletonRepository: LeaderboardRepository | null | undefined;
let emulatorConnected = false;

function toDate(value: unknown): Date | null {
  if (
    typeof value !== "object" ||
    value === null ||
    !("toDate" in value) ||
    typeof value.toDate !== "function"
  ) {
    return null;
  }
  const date = value.toDate();
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

function safeScore(value: unknown): number | null {
  return Number.isSafeInteger(value) &&
    Number(value) >= 0 &&
    Number(value) <= GAME_RESULT_LIMITS.maxScore
    ? Number(value)
    : null;
}

export function mapLeaderboardDocument(
  id: string,
  data: DocumentData,
  rank: number,
): LeaderboardEntry {
  const score = safeScore(data.bestScore);
  const achievedAt = toDate(data.achievedAt);
  if (score === null || !achievedAt) {
    throw new Error("A toplista egyik rekordja érvénytelen.");
  }
  if (data.playerType !== "guest" && data.playerType !== "player") {
    throw new Error("A toplista egyik játékostípusa érvénytelen.");
  }

  const alias = typeof data.publicAlias === "string" ? data.publicAlias.trim() : "";
  return {
    id,
    rank,
    alias: alias || "Ismeretlen játékos",
    playerType: data.playerType,
    score,
    achievedAt,
  };
}

function repositoryErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return error instanceof Error
      ? error.message
      : "A toplistát most nem sikerült betölteni.";
  }

  switch (error.code) {
    case "permission-denied":
      return "A toplista olvasási jogosultsága hibásan van beállítva.";
    case "failed-precondition":
      return "A toplista adatbázis-indexe még nem áll készen.";
    case "unavailable":
      return "A toplista szolgáltatása most nem érhető el. Próbáld újra később.";
    default:
      return "A toplistát most nem sikerült betölteni.";
  }
}

class FirestoreLeaderboardRepository implements LeaderboardRepository {
  constructor(private readonly firestore: Firestore) {}

  async loadTopTen(): Promise<LeaderboardEntry[]> {
    try {
      const leaderboardQuery = query(
        collection(this.firestore, "publicLeaderboard"),
        orderBy("bestScore", "desc"),
        orderBy("achievedAt", "asc"),
        limit(10),
      );
      const snapshot = await getDocs(leaderboardQuery);
      return snapshot.docs.map((entry, index) =>
        mapLeaderboardDocument(entry.id, entry.data(), index + 1),
      );
    } catch (error) {
      throw new Error(repositoryErrorMessage(error));
    }
  }

  async loadOwnScore(uid: string): Promise<OwnScoreSummary | null> {
    try {
      const snapshot = await getDoc(doc(this.firestore, "players", uid));
      if (!snapshot.exists()) {
        return null;
      }
      const data = snapshot.data();
      return {
        bestScore: safeScore(data.privateBestScore),
        bestAchievedAt: toDate(data.privateBestAchievedAt),
        latestScore: safeScore(data.privateLatestScore),
        latestFinishedAt: toDate(data.privateLatestFinishedAt),
      };
    } catch (error) {
      throw new Error(repositoryErrorMessage(error));
    }
  }
}

function createLeaderboardRepository(): LeaderboardRepository | null {
  const client = getFirebaseClient();
  if (!client) {
    return null;
  }

  const firestore = getFirestore(client.app);
  if (client.settings.useFirestoreEmulator && !emulatorConnected) {
    connectFirestoreEmulator(
      firestore,
      client.settings.firestoreEmulatorHost,
      client.settings.firestoreEmulatorPort,
    );
    emulatorConnected = true;
  }
  return new FirestoreLeaderboardRepository(firestore);
}

export function getLeaderboardRepository(): LeaderboardRepository | null {
  if (singletonRepository === undefined) {
    singletonRepository = createLeaderboardRepository();
  }
  return singletonRepository;
}
